import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import BookingModal from '../component/Booking/BookingModal';

const CLINIKO_BOOKING_URL = 'https://cornerstone-physiotherapy-aveley.au1.cliniko.com/bookings?_gl=1*1ope42w*_gcl_au*MjEwNzE0MDUzOC4xNzcyNjExODA1*_ga*NTM2MDg1ODcwLjE3NzI2MTE4MDU.*_ga_NRKEH5MSX4*czE3NzY1ODg1NTAkbzMkZzAkdDE3NzY1ODg1NTAkajYwJGwwJGgw#service';

const BookingModalContext = createContext();

export const useBookingModal = () => {
  const context = useContext(BookingModalContext);
  if (!context) {
    throw new Error('useBookingModal must be used within BookingModalProvider');
  }
  return context;
};

// Floating mobile CTA button - rendered via portal to bypass stacking context issues
const MobileBookingButton = ({ onClick, isModalOpen }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isMobile || isModalOpen) return null;

  const buttonStyle = {
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    background: '#509fd7',
    color: '#ffffff',
    border: 'none',
    borderRadius: '50px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(80, 159, 215, 0.5)',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
  };

  return ReactDOM.createPortal(
    <button
      type="button"
      style={buttonStyle}
      onClick={onClick}
      aria-label="Book an Appointment"
    >
      Book an Appointment
    </button>,
    document.body
  );
};

export const BookingModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = useCallback(() => {
    window.open(CLINIKO_BOOKING_URL, '_blank', 'noopener,noreferrer');
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Listen for custom event (legacy support)
  useEffect(() => {
    const handleOpenModal = () => openModal();
    window.addEventListener('openBookingModal', handleOpenModal);
    return () => window.removeEventListener('openBookingModal', handleOpenModal);
  }, [openModal]);

  return (
    <BookingModalContext.Provider value={{ isOpen, openModal, closeModal }}>
      {children}
      <MobileBookingButton onClick={openModal} isModalOpen={false} />
    </BookingModalContext.Provider>
  );
};
